import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Input, Segmented, Select, Spin, Switch, Typography, message } from 'antd';
import { LockKeyIcon, ProhibitIcon, ShieldCheckIcon } from '@phosphor-icons/react';
import { CATEGORIES, catalogTypes, errorText, normalizeOffers, typeDef } from './accountTypes';
import { ProviderIcon, TestResult } from './AccountParts';
import { useNarrow } from './useAccounts';
import { addTokenAccount, replaceToken, startOauth, testAccount } from '../../services/AccountsService';

/** Where the provider sends the browser back to: this page, flagged so it can say the account is linked. */
export function returnUriFor() {
  const basePath = (import.meta.env?.BASE_URL || '/').replace(/\/?$/, '/');
  return `${window.location.origin}${basePath}features/connections?account_linked=1#your-accounts`;
}

const OTHER = '__other__';
const lower = (text) => text.charAt(0).toLowerCase() + text.slice(1);

/** The option values to start from: the offer's (an admin app's), else each option's default. */
const optionDefaults = (options, offer) => Object.fromEntries(options.map((o) => [
  o.key,
  offer?.options?.[o.key] ?? o.default ?? (o.kind === 'bool' ? false : ''),
]));

/** What is sent: booleans as they are, strings trimmed, empty strings left out. */
const cleanOptions = (options, values) => Object.fromEntries(options
  .map((o) => [o.key, o.kind === 'bool' ? Boolean(values[o.key]) : String(values[o.key] ?? '').trim()])
  .filter(([, v]) => v !== ''));

/** The types grouped by category, in the catalog's order (an unknown category goes last, under its own name). */
function groupTypes(types) {
  const known = CATEGORIES.map((c) => ({ ...c, types: types.filter((t) => t.category === c.key) }));
  const others = [...new Set(types.map((t) => t.category))].filter((c) => !CATEGORIES.some((k) => k.key === c))
    .map((c) => ({ key: c, label: c, types: types.filter((t) => t.category === c) }));
  return [...known, ...others].filter((g) => g.types.length);
}

/**
 * Add an account (pick a type from the catalog: git hosting, package registry or tool; then Connect with OAuth when
 * the admin set up an OAuth app for it, or use a token) or Replace one (a new token for a token account, a new OAuth
 * link for an OAuth one). The token lives in this form only until it is sent: it is cleared whatever the answer, and
 * never stored anywhere in the browser.
 */
export default function AccountDrawer({ open, onClose, providers = [], catalog = null, replace = null, onSaved, disabled = false }) {
  const narrow = useNarrow();
  const types = useMemo(() => catalogTypes(catalog), [catalog]);
  const offers = useMemo(() => normalizeOffers(providers), [providers]);
  const groups = useMemo(() => groupTypes(types), [types]);
  const defFor = (key) => types.find((t) => t.key === key) || typeDef(key);
  const firstType = () => replace?.type || types[0]?.key || 'github';

  const [type, setType] = useState(firstType);
  const [method, setMethod] = useState(replace?.method || 'token');
  const [name, setName] = useState('');
  const [server, setServer] = useState(OTHER); // an offer's id, or OTHER: the URL typed in `baseUrl`
  const [baseUrl, setBaseUrl] = useState('');
  const [username, setUsername] = useState('');
  const [options, setOptions] = useState({});
  const [token, setToken] = useState('');
  const [oauthId, setOauthId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  const [testing, setTesting] = useState(false);

  const def = defFor(type);
  const ofType = offers.filter((o) => o.type === type);
  const oauthOffers = ofType.filter((o) => o.enabled && o.mode === 'oauth');
  const disabledOffers = ofType.filter((o) => !o.enabled);
  const serverOffers = ofType.filter((o) => o.enabled && o.baseUrl);
  const fixed = def.fixedBaseUrl;
  const chosen = fixed ? (serverOffers[0] || null) : (serverOffers.find((o) => o.id === server) || null);
  const oauthOffer = oauthOffers.find((o) => o.id === oauthId) || oauthOffers[0] || null;
  const canOauth = replace ? replace.method === 'oauth' : oauthOffers.length > 0;
  const showUsername = def.tokenFields.includes('username') || Boolean(def.optionalUsername);
  const usernameRequired = def.tokenFields.includes('username');
  const fromApp = Boolean(chosen?.appId);

  /** Fields for a newly picked type: its first server offer, its default username and options, its first OAuth app. */
  const resetFor = (key, nextMethod) => {
    const d = defFor(key);
    const mine = offers.filter((o) => o.type === key);
    const servers = mine.filter((o) => o.enabled && o.baseUrl);
    const oauthable = mine.filter((o) => o.enabled && o.mode === 'oauth');
    setType(key);
    setServer(servers[0]?.id || OTHER);
    setBaseUrl('');
    setUsername(d.defaultUsername || '');
    setOptions(optionDefaults(d.options, servers[0]?.appId ? servers[0] : null));
    setOauthId(oauthable[0]?.id || '');
    // Replace keeps the account's method; otherwise an enabled OAuth app is the default way to connect.
    const wanted = replace ? nextMethod : (oauthable.length ? 'oauth' : 'token');
    setMethod(wanted === 'oauth' && (replace || oauthable.length) ? 'oauth' : 'token');
    setError(null);
  };

  useEffect(() => {
    if (!open) { return; }
    resetFor(firstType(), replace?.method || 'token');
    setName('');
    setToken('');
    setSaved(null);
    setBusy(false);
    // Only when the drawer opens (or what it replaces changes): a background refresh must not wipe what is typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, replace]);

  const chooseServer = (value) => {
    setServer(value);
    const offer = serverOffers.find((o) => o.id === value);
    setOptions(optionDefaults(def.options, offer?.appId ? offer : null));
  };

  const title = replace ? `Replace ${replace.label || replace.name}` : 'Add an account';

  const connect = async() => {
    setBusy(true);
    setError(null);
    try {
      const { authorizationUrl } = await startOauth({
        type,
        appId: replace ? replace.appId : oauthOffer?.appId,
        host: replace ? replace.host : oauthOffer?.host,
        name: name.trim() || undefined,
        returnUri: returnUriFor(),
        replaceId: replace?.id,
      });
      window.location.assign(authorizationUrl);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  /** The base URL to send, or an error to show. */
  const resolveBaseUrl = () => {
    if (fixed) { return { url: chosen?.baseUrl || fixed }; }
    if (chosen) { return { url: chosen.baseUrl }; }
    const typed = baseUrl.trim();
    if (!typed && def.baseUrlRequired) { return { error: `Enter the ${lower(def.baseUrlLabel)}.` }; }
    if (typed && !/^https?:\/\//i.test(typed)) { return { error: `The ${lower(def.baseUrlLabel)} starts with https://` }; }
    return { url: typed || def.defaultBaseUrl || undefined };
  };

  const store = async(event) => {
    event?.preventDefault?.();
    const value = token.trim();
    if (!value) { setError('Paste the token.'); return; }
    let url;
    if (!replace) {
      const resolved = resolveBaseUrl();
      if (resolved.error) { setError(resolved.error); return; }
      url = resolved.url;
      if (usernameRequired && !username.trim()) { setError(`Enter the ${lower(def.usernameLabel)}.`); return; }
    }
    setBusy(true);
    setError(null);
    setToken(''); // never kept, whatever the answer
    try {
      const answer = replace
        ? await replaceToken(replace.id, value)
        : await addTokenAccount({
          type,
          baseUrl: url,
          token: value,
          name: name.trim() || undefined,
          username: showUsername ? (username.trim() || undefined) : undefined,
          options: def.options.length ? cleanOptions(def.options, options) : undefined,
          appId: chosen?.appId || undefined,
        });
      setSaved(answer);
      message.success(replace ? `${answer.account.label} has a new token` : `${answer.account.label} stored in OpenBao`);
      onSaved?.();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const testAgain = async() => {
    if (!saved) { return; }
    setTesting(true);
    try {
      const answer = await testAccount(saved.account.id);
      setSaved(answer);
      onSaved?.();
    } catch (err) {
      setSaved({ ...saved, test: { ok: false, at: Date.now(), message: errorText(err) } });
    } finally {
      setTesting(false);
    }
  };

  const oauthHelp = () => {
    if (!def.oauth) { return `${def.label} connects with a token.`; }
    if (disabledOffers.some((o) => o.mode === 'oauth')) { return `Your admin disabled the ${def.label} OAuth app: use a token instead.`; }
    return `Flow has no ${def.label} OAuth app yet: use a token instead.`;
  };

  const extra = saved
    ? <Button onClick={onClose} type="primary">Done</Button>
    : (
      <div className="acct-drawer-actions">
        <Button onClick={onClose}>Cancel</Button>
        {method === 'token' && (
          <Button disabled={disabled || busy} htmlType="submit" form="acct-token-form" icon={<LockKeyIcon aria-hidden size={16} />} loading={busy} type="primary">
            {replace ? 'Replace token' : 'Store in OpenBao'}
          </Button>
        )}
      </div>
    );

  const nameField = !replace && (
    <label className="acct-field">
      <span className="acct-field-label">Name (optional)</span>
      <Input aria-label="Name" onChange={(e) => setName(e.target.value)} placeholder={`${def.label} work`} value={name} />
    </label>
  );

  const urlInput = (
    <Input
      aria-label={def.baseUrlLabel}
      inputMode="url"
      onChange={(e) => setBaseUrl(e.target.value)}
      placeholder={def.baseUrlPlaceholder}
      value={baseUrl}
    />
  );

  const baseUrlField = !replace && (
    <div className="acct-field">
      <span className="acct-field-label">{def.baseUrlLabel}{!fixed && !def.baseUrlRequired ? ' (optional)' : ''}</span>
      {fixed && <span className="acct-fixed-url">{fixed}</span>}
      {!fixed && serverOffers.length > 0 && (
        <Select
          aria-label={def.category === 'registry' ? 'Registry' : 'Server'}
          onChange={chooseServer}
          options={[
            ...serverOffers.map((o) => ({ value: o.id, label: `${o.name} · ${o.baseUrl}` })),
            { value: OTHER, label: 'Other server…' },
          ]}
          value={chosen ? chosen.id : OTHER}
        />
      )}
      {!fixed && !chosen && urlInput}
      {!fixed && !chosen && def.baseUrlHelp && <span className="acct-field-help">{def.baseUrlHelp}</span>}
      {fromApp && <span className="acct-field-help">Set up by your admin: Flow uses this app&apos;s address and settings.</span>}
    </div>
  );

  const optionFields = !replace && def.options.map((o) => (
    o.kind === 'bool' ? (
      <div className="acct-field acct-switch-field" key={o.key}>
        <span className="acct-switch-row">
          <Switch aria-label={o.label} checked={Boolean(options[o.key])} disabled={fromApp} onChange={(v) => setOptions({ ...options, [o.key]: v })} size="small" />
          <span>{o.label}</span>
        </span>
        {def.optionHelp[o.key] && <span className="acct-field-help">{def.optionHelp[o.key]}</span>}
      </div>
    ) : (
      <label className="acct-field" key={o.key}>
        <span className="acct-field-label">{o.label}</span>
        <Input aria-label={o.label} disabled={fromApp} onChange={(e) => setOptions({ ...options, [o.key]: e.target.value })} value={options[o.key] ?? ''} />
        {def.optionHelp[o.key] && <span className="acct-field-help">{def.optionHelp[o.key]}</span>}
      </label>
    )
  ));

  return (
    <Drawer destroyOnHidden extra={extra} onClose={onClose} open={open} size={narrow ? '100%' : 960} title={title}>
      <div className="acct-drawer-body">
        <div className="acct-drawer-form">
          {error && <Alert closable onClose={() => setError(null)} showIcon title={method === 'oauth' ? 'Not connected' : 'Not stored'} description={error} type="error" />}
          {saved ? (
            <div className="acct-saved">
              <Alert
                description={`${saved.account.label} on ${saved.account.host}${saved.account.last4 ? ` (••••${saved.account.last4})` : ''}. Flow does not keep the token in the browser and will never show it again.`}
                showIcon
                title={`${saved.account.name} saved`}
                type="success"
              />
              {testing ? <div className="acct-testing"><Spin size="small" /> Testing the connection (read-only)…</div> : <TestResult outcome={saved.test} />}
              <div><Button loading={testing} onClick={testAgain}>Test again</Button></div>
            </div>
          ) : (
            <>
              {!replace && (
                <div className="acct-field">
                  <span className="acct-field-label" id="acct-type-label">Type</span>
                  <div aria-labelledby="acct-type-label" className="acct-type-groups" role="radiogroup">
                    {groups.map((g) => (
                      <div aria-label={g.label} className="acct-type-group" key={g.key} role="group">
                        <span aria-hidden className="acct-type-group-label">{g.label}</span>
                        <div className="acct-type-grid">
                          {g.types.map((t) => (
                            <button
                              aria-checked={type === t.key}
                              aria-label={t.label}
                              className={`acct-type-option${type === t.key ? ' acct-type-option-on' : ''}`}
                              key={t.key}
                              onClick={() => { if (t.key !== type) { resetFor(t.key, method); } }}
                              role="radio"
                              type="button"
                            >
                              <ProviderIcon category={t.category} size={20} type={t.key} /> {t.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!replace && (
                <div className="acct-field">
                  <span className="acct-field-label">How it signs in</span>
                  {def.oauth && (
                    <Segmented
                      block={narrow}
                      onChange={setMethod}
                      options={[
                        { value: 'oauth', label: 'Connect with OAuth', disabled: !canOauth },
                        { value: 'token', label: 'Use a token instead' },
                      ]}
                      value={method}
                    />
                  )}
                  {!canOauth && <span className="acct-field-help">{oauthHelp()}</span>}
                  {disabledOffers.length > 0 && (
                    <ul aria-label="Disabled apps" className="acct-offers-off">
                      {disabledOffers.map((o) => (
                        <li key={o.id}>
                          <ProhibitIcon aria-hidden size={14} /> <span>{o.name}{o.host ? ` · ${o.host}` : ''}: Disabled by your admin</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {method === 'oauth' ? (
                <div className="acct-oauth">
                  {nameField}
                  {!replace && oauthOffers.length > 1 && (
                    <label className="acct-field">
                      <span className="acct-field-label">{def.label} app</span>
                      <Select
                        aria-label="OAuth app"
                        onChange={setOauthId}
                        options={oauthOffers.map((o) => ({ value: o.id, label: `${o.name} · ${o.host}` }))}
                        value={oauthOffer?.id}
                      />
                    </label>
                  )}
                  <p className="acct-field-help">
                    {replace
                      ? `You will sign in to ${replace.host} again; the new link replaces this account's.`
                      : `You will sign in to ${oauthOffer?.host || def.label} and approve Flow; you come back here once it is done.`}
                  </p>
                  <div>
                    <Button disabled={disabled || busy} icon={<ProviderIcon category={def.category} size={16} type={type} />} loading={busy} onClick={connect} type="primary">
                      {replace ? `Reconnect ${def.label}` : `Connect ${def.label}`}
                    </Button>
                  </div>
                </div>
              ) : (
                <form autoComplete="off" className="acct-token-form" id="acct-token-form" onSubmit={store}>
                  {nameField}
                  {baseUrlField}
                  {replace && <p className="acct-field-help">A new token for {replace.username || replace.login} on {replace.host}. It must belong to the same account.</p>}
                  {!replace && showUsername && (
                    <label className="acct-field">
                      <span className="acct-field-label">{def.usernameLabel}</span>
                      <Input aria-label="Username" autoComplete="off" onChange={(e) => setUsername(e.target.value)} value={username} />
                    </label>
                  )}
                  <label className="acct-field">
                    <span className="acct-field-label">{def.tokenLabel || 'Token'}</span>
                    <Input.Password aria-label="Token" autoComplete="off" onChange={(e) => setToken(e.target.value)} placeholder={def.tokenPlaceholder} value={token} />
                  </label>
                  {optionFields}
                  <div className="acct-write-only">
                    <ShieldCheckIcon aria-hidden size={18} />
                    <span>Write-only: the token goes straight to OpenBao in this request. Flow never shows it again and clears this form after saving. Then it runs one read-only test call.</span>
                  </div>
                  {narrow && (
                    <Button block disabled={disabled || busy} htmlType="submit" loading={busy} type="primary">{replace ? 'Replace token' : 'Store in OpenBao'}</Button>
                  )}
                </form>
              )}
            </>
          )}
        </div>

        <aside aria-label={`${def.label}: where to get it`} className="acct-help">
          <span className="acct-help-title"><ProviderIcon category={def.category} size={18} type={type} /> {def.label}</span>
          <Typography.Text type="secondary">{def.usedFor}</Typography.Text>
          <Typography.Text strong>Where to get it</Typography.Text>
          <ol className="acct-help-list">{def.where.map((w) => <li key={w}>{w}</li>)}</ol>
          <Typography.Text strong>Least privilege</Typography.Text>
          <Typography.Text type="secondary">{def.grant}</Typography.Text>
          {def.commands && <Typography.Paragraph className="acct-code" copyable={{ text: def.commands }}>{def.commands}</Typography.Paragraph>}
        </aside>
      </div>
    </Drawer>
  );
}
